import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CoinCelebration from "@/components/CoinCelebration";
import { useAuth } from "@/contexts/AuthContext";
import HighStrikerCelebration from "@/components/HighStrikerCelebration";
import PunkRockerCelebration from "@/components/PunkRockerCelebration";
import SkateboarderCelebration from "@/components/SkateboarderCelebration";
import HeavyBagCelebration from "@/components/HeavyBagCelebration";
import DragonHoardCelebration from "@/components/DragonHoardCelebration";
import RocketLaunchCelebration from "@/components/RocketLaunchCelebration";

// Coin-set rotation: the first 3 lessons always play the original coin shower.
// After that, a shuffled bag rotates evenly through the other coin sets
// (high striker, punk rocker, skateboarder) so no set repeats too often.
// MILESTONES: starting at 20 completed lessons and every 10 after (20, 30, 40…),
// one of the special sets (heavy bag, dragon hoard, rocket launch) plays instead.
const BAG_KEY = "coinSetBag";
const OTHER_SETS = ["striker", "rocker", "skater"];
const SPECIAL_BAG_KEY = "coinSetSpecialBag";
const SPECIAL_SETS = ["heavybag", "dragon", "rocket"];

function pickFromBag(key, sets) {
  let bag = [];
  try { bag = JSON.parse(localStorage.getItem(key)) || []; } catch { /* fresh bag */ }
  bag = bag.filter((s) => sets.includes(s));
  if (!bag.length) bag = [...sets].sort(() => Math.random() - 0.5);
  const pick = bag.shift();
  try { localStorage.setItem(key, JSON.stringify(bag)); } catch { /* non-blocking */ }
  return pick;
}

function nextCoinSet(completedCount) {
  const justCompleted = completedCount + 1;
  if (justCompleted >= 20 && justCompleted % 10 === 0) return pickFromBag(SPECIAL_BAG_KEY, SPECIAL_SETS);
  if (completedCount < 3) return "coins";
  return pickFromBag(BAG_KEY, OTHER_SETS);
}

// The set plays inside a FIXED overlay anchored to the top of the screen — fully
// detached from the page flow, so nothing (counters wrapping, banners mounting,
// course data reloading) can move or bounce it. Page scroll is locked underneath.
export default function LessonCelebration({ completedCount = 0, onDone, ...props }) {
  const { user } = useAuth();
  // Gem Shop perk: an equipped celebration always plays instead of the rotation.
  const [pick] = useState(() => user?.selected_celebration || nextCoinSet(completedCount));
  const [gender] = useState(() => (Math.random() < 0.5 ? "boy" : "girl"));
  const [hidden, setHidden] = useState(false);
  // Sets without action buttons (no-quiz lessons) auto-dismiss shortly after finishing.
  const hasButtons = !!(props.onContinue || props.onRetry || props.onPrev);

  // Lock page scroll while the set is on screen. Keyed on `hidden` because
  // auto-dismissing sets return null while staying mounted — the lock must release
  // the moment the overlay hides, and always restores to "" (a captured previous
  // value could itself be a stale "hidden" from an overlapping overlay).
  useEffect(() => {
    if (hidden) return undefined;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [hidden]);

  const handleDone = () => {
    onDone && onDone();
    if (!hasButtons) setTimeout(() => setHidden(true), 1600);
  };

  if (hidden) return null;
  const setProps = { ...props, onDone: handleDone };
  const inner =
    pick === "rocker" ? <PunkRockerCelebration {...setProps} gender={gender} />
    : pick === "striker" ? <HighStrikerCelebration {...setProps} />
    : pick === "skater" ? <SkateboarderCelebration {...setProps} gender={gender} />
    : pick === "heavybag" ? <HeavyBagCelebration {...setProps} gender={gender} />
    : pick === "dragon" ? <DragonHoardCelebration {...setProps} />
    : pick === "rocket" ? <RocketLaunchCelebration {...setProps} />
    : <CoinCelebration {...setProps} />;

  // Sized/positioned against the VISUAL viewport (--vvh/--vvt from lib/viewport.js) so the
  // set stays fully on screen on phones — even when iOS zooms or the keyboard shifted the view.
  // The card vertically centers when it fits (m-auto) and scrolls when taller than the screen.
  return createPortal(
    <div className="fixed z-[70] bg-black/70 backdrop-blur-sm overflow-y-auto overscroll-contain"
      style={{ top: "var(--vvt, 0px)", left: "var(--vvl, 0px)",
               width: "var(--vvw, 100vw)", height: "var(--vvh, 100dvh)" }}
      data-testid="celebration-overlay">
      <div className="min-h-full flex flex-col items-center p-3">
        <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl px-3 py-4 my-auto">
          {inner}
        </div>
      </div>
    </div>,
    document.body
  );
}
